import { Link, useLocation } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { AUTHENTICATED_NAV } from '../../lib/shell/navigation';

/**
 * Navigation for the Application Shell (U1a).
 *
 * The Application Shell had none. Its header was a brand mark and an auth
 * control, so a signed-in visitor moved between their own surfaces with the
 * back button. The shell owns this rather than the dashboard page owning it:
 * navigation that exists on one page is a page, not a home.
 *
 * **Rendered only when signed in.** Nothing here is secret, but advertising
 * authenticated destinations to a signed-out visitor offers routes that will
 * only show them a sign-in prompt. `password-recovery` is excluded for the
 * reason `RequireAuth` states: it is a valid token, not a signed-in session,
 * and its only forward path is finishing the reset.
 *
 * **Suppressed on the assessment runner.** Legacy deliberately minimised
 * navigation during an assessment, and the runner is fifty questions long — an
 * exit offered at question thirty is an abandonment invitation. The suppression
 * is matched on the `/assessment/` prefix rather than on the three known type
 * paths, so a fourth assessment type inherits it without this file changing.
 */
const ASSESSMENT_PREFIX = '/assessment/';

/** Exported for the shell tests, which assert the rule rather than a snapshot
 *  of the paths it happens to match today. */
export function suppressesNav(path: string): boolean {
  return path.startsWith(ASSESSMENT_PREFIX);
}

export function AppNav() {
  const { status } = useAuth();
  const [location] = useLocation();

  if (status !== 'signed-in') return null;
  if (suppressesNav(location)) return null;

  return (
    <nav
      aria-label="Account"
      data-testid="app-nav"
      className="flex items-center gap-5 text-sm text-muted-foreground"
    >
      {AUTHENTICATED_NAV.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}
